import {
  Badge,
  Box,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Switch,
  FormControl,
  FormLabel,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MdScience,
  MdZoomIn,
  MdZoomOut,
  MdCenterFocusStrong,
  MdFullscreen,
  MdInfo,
} from 'react-icons/md'
import { IAnalysisMalaria } from '../../../types/ia-analysis'
import { useUpdateMalariaBoxes } from '../../../api/api_hooks/lab/directMalaria'

const isUploadUrl = (url: string) =>
  Boolean(url) && !url.startsWith('blob:') && !url.startsWith('data:') && url.includes('/uploads/')

// Fetch a server upload URL with JWT auth and return a blob: URL.
// Returns null while loading (prevents the unauthenticated img request that causes 404).
const useAuthenticatedImageUrl = (imageUrl: string): string | null => {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(() =>
    isUploadUrl(imageUrl) ? null : imageUrl
  )

  useEffect(() => {
    if (!isUploadUrl(imageUrl)) {
      setResolvedUrl(imageUrl)
      return
    }

    let cancelled = false
    let objectUrl: string | null = null

    setResolvedUrl(null)

    const load = async () => {
      try {
        const apiUrl = imageUrl.replace(/\/uploads\//, '/api/uploads/')
        const authData = JSON.parse(localStorage.getItem('auth') || '{}')
        const token: string | undefined = authData.accessToken
        const response = await fetch(apiUrl, {
          headers: token ? { authorization: token } : {},
        })
        if (!response.ok || cancelled) return
        const blob = await response.blob()
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setResolvedUrl(objectUrl)
      } catch (_e) {
        if (!cancelled) setResolvedUrl(imageUrl)
      }
    }

    load()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [imageUrl])

  return resolvedUrl
}

// Coords are [x1, y1, x2, y2] — backend may append confidence as 5th element [x1, y1, x2, y2, conf]
type BoundingBox = [number, number, number, number]
type RawBox = number[]

interface DetectionBox {
  coords: BoundingBox
  type: 'positive' | 'negative'
  confidence?: number
  index: number
}

interface AnalysisImagePreviewProps {
  imageUrl: string
  result?: IAnalysisMalaria
  imageIndex: number
  showOnlyInfected: boolean
  highlightBothStates?: boolean
  isAnalyzing?: boolean
  onBoxLabelChange?: (
    imageIndex: number,
    newPositiveCount: number,
    newNegativeCount: number,
    newPositiveBoxes: number[][],
    newNegativeBoxes: number[][]
  ) => void
}

const AnalysisImagePreview = ({
  imageUrl,
  result,
  imageIndex,
  showOnlyInfected,
  highlightBothStates = true,
  isAnalyzing = false,
  onBoxLabelChange,
}: AnalysisImagePreviewProps) => {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const { mutateAsync: updateBoxes } = useUpdateMalariaBoxes()
  const resolvedImageUrl = useAuthenticatedImageUrl(imageUrl)

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [fallbackError, setFallbackError] = useState(false)
  const [zoom, setZoom] = useState(1.0)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [hoveredBox, setHoveredBox] = useState<number | null>(null)
  const [showPositive, setShowPositive] = useState(true)
  const [showNegative, setShowNegative] = useState(true)
  const { isOpen, onOpen, onClose } = useDisclosure()

  // Drag-to-reposition state
  const [draggingBoxInfo, setDraggingBoxInfo] = useState<{
    detection: DetectionBox
    startMouseX: number
    startMouseY: number
    originalBox: RawBox
    hasMoved: boolean
  } | null>(null)
  const [dragOffsetPx, setDragOffsetPx] = useState({ dx: 0, dy: 0 })
  const wasDraggingRef = useRef(false)

  // État local pour tracker les boxes modifiées (raw = may include confidence as 5th element)
  const [localPositiveBoxes, setLocalPositiveBoxes] = useState<RawBox[]>([])
  const [localNegativeBoxes, setLocalNegativeBoxes] = useState<RawBox[]>([])

  // Initialiser les boxes locales quand le résultat change
  useEffect(() => {
    const boxes = (result?.positiveBoxes ?? []).filter(
      (coords): coords is RawBox =>
        Array.isArray(coords) && coords.length >= 4 && coords.every((value) => typeof value === 'number')
    )
    setLocalPositiveBoxes(boxes)
  }, [result?.positiveBoxes])

  useEffect(() => {
    const boxes = (result?.negativeBoxes ?? []).filter(
      (coords): coords is RawBox =>
        Array.isArray(coords) && coords.length >= 4 && coords.every((value) => typeof value === 'number')
    )
    setLocalNegativeBoxes(boxes)
  }, [result?.negativeBoxes])

  // Utiliser directement les boxes locales
  const positiveBoxes = localPositiveBoxes
  const negativeBoxes = localNegativeBoxes

  // Build detection boxes with metadata
  const allDetections = useMemo((): DetectionBox[] => {
    const detections: DetectionBox[] = []

    if (showPositive) {
      positiveBoxes.forEach((raw, index) => {
        detections.push({
          coords: [raw[0], raw[1], raw[2], raw[3]] as BoundingBox,
          type: 'positive',
          confidence: raw.length >= 5 ? raw[4] : undefined, // Real CNN confidence if available
          index,
        })
      })
    }

    if (showNegative && !showOnlyInfected) {
      negativeBoxes.forEach((raw, index) => {
        detections.push({
          coords: [raw[0], raw[1], raw[2], raw[3]] as BoundingBox,
          type: 'negative',
          confidence: raw.length >= 5 ? raw[4] : undefined, // Real CNN confidence if available
          index,
        })
      })
    }

    return detections
  }, [positiveBoxes, negativeBoxes, showOnlyInfected, showPositive, showNegative])

  // Calculate actual image dimensions to use for coordinate mapping
  const imageDimensions = useMemo(() => {
    if (dimensions.width === 0 || dimensions.height === 0) {
      return { width: 640, height: 480 } // Fallback
    }
    return dimensions
  }, [dimensions])

  // Detect if coordinates are normalized (0-1) or absolute pixels
  const areCoordinatesNormalized = useMemo(() => {
    if (allDetections.length === 0) return true

    // Check a sample of coordinates
    const samples = allDetections.slice(0, 5)
    let normalizedCount = 0

    samples.forEach(({ coords }) => {
      const [x1, y1, x2, y2] = coords
      const maxX = Math.max(Math.abs(x1), Math.abs(x2))
      const maxY = Math.max(Math.abs(y1), Math.abs(y2))

      if (maxX <= 1 && maxY <= 1) {
        normalizedCount++
      }
    })

    return normalizedCount >= samples.length / 2
  }, [allDetections])

  // Pan/Zoom handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) { // Left click only
      setIsDragging(true)
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
    }
  }, [pan])

  // Start dragging a box to reposition it
  const handleBoxMouseDown = useCallback((detection: DetectionBox, e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    wasDraggingRef.current = false
    const rawBox = detection.type === 'positive'
      ? positiveBoxes[detection.index]
      : negativeBoxes[detection.index]
    setDraggingBoxInfo({
      detection,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      originalBox: rawBox,
      hasMoved: false,
    })
  }, [positiveBoxes, negativeBoxes])

  // Apply final box reposition: convert screen delta → image coords, update state + DB
  const applyBoxReposition = useCallback((
    info: NonNullable<typeof draggingBoxInfo>,
    offsetPx: { dx: number; dy: number }
  ) => {
    const imgEl = imageRef.current
    if (!imgEl) return

    // Screen delta → rendered image delta (cancel zoom)
    const renderedDx = offsetPx.dx / zoom
    const renderedDy = offsetPx.dy / zoom

    // Rendered delta → image coord delta
    const renderedWidth = imgEl.width || imageDimensions.width
    const renderedHeight = imgEl.height || imageDimensions.height
    let imageDx: number, imageDy: number
    if (areCoordinatesNormalized) {
      imageDx = renderedDx / renderedWidth
      imageDy = renderedDy / renderedHeight
    } else {
      imageDx = renderedDx / (renderedWidth / imageDimensions.width)
      imageDy = renderedDy / (renderedHeight / imageDimensions.height)
    }

    const { originalBox, detection } = info
    const newBox: RawBox = [
      originalBox[0] + imageDx,
      originalBox[1] + imageDy,
      originalBox[2] + imageDx,
      originalBox[3] + imageDy,
      ...(originalBox.length >= 5 ? [originalBox[4]] : []),
    ]

    const newPositiveBoxes = detection.type === 'positive'
      ? positiveBoxes.map((b, i) => (i === detection.index ? newBox : b))
      : [...positiveBoxes]
    const newNegativeBoxes = detection.type === 'negative'
      ? negativeBoxes.map((b, i) => (i === detection.index ? newBox : b))
      : [...negativeBoxes]

    setLocalPositiveBoxes(newPositiveBoxes)
    setLocalNegativeBoxes(newNegativeBoxes)
    onBoxLabelChange?.(imageIndex, newPositiveBoxes.length, newNegativeBoxes.length, newPositiveBoxes, newNegativeBoxes)

    if (result?.id) {
      void updateBoxes({
        imageId: result.id,
        positiveBoxes: newPositiveBoxes.map((b) => [b[0], b[1], b[2], b[3]]),
        negativeBoxes: newNegativeBoxes.map((b) => [b[0], b[1], b[2], b[3]]),
      })
    }
  }, [zoom, imageDimensions, areCoordinatesNormalized, positiveBoxes, negativeBoxes, imageIndex, onBoxLabelChange, result?.id, updateBoxes])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (draggingBoxInfo) {
      const dx = e.clientX - draggingBoxInfo.startMouseX
      const dy = e.clientY - draggingBoxInfo.startMouseY
      if (!draggingBoxInfo.hasMoved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
        wasDraggingRef.current = true
        setDraggingBoxInfo(prev => prev ? { ...prev, hasMoved: true } : null)
      }
      setDragOffsetPx({ dx, dy })
      return
    }
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    }
  }, [draggingBoxInfo, isDragging, dragStart])

  const handleMouseUp = useCallback(() => {
    if (draggingBoxInfo) {
      if (draggingBoxInfo.hasMoved) {
        applyBoxReposition(draggingBoxInfo, dragOffsetPx)
      }
      setDraggingBoxInfo(null)
      setDragOffsetPx({ dx: 0, dy: 0 })
      return
    }
    setIsDragging(false)
  }, [draggingBoxInfo, dragOffsetPx, applyBoxReposition])

  // Zoom only via slider/buttons, scroll wheel passes through to page

  const resetView = () => {
    setZoom(1.0)
    setPan({ x: 0, y: 0 })
  }

  // Fonction pour basculer le label d'une box
  const handleBoxClick = useCallback(async (detection: DetectionBox, e: React.MouseEvent) => {
    e.stopPropagation()

    // Skip if this was the end of a drag operation
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false
      return
    }

    // Keep original raw box (with confidence if present) for the switch
    const rawBox = detection.type === 'positive'
      ? positiveBoxes[detection.index]
      : negativeBoxes[detection.index]
    // Keep coords + confidence if present (preserve model confidence even when overriding label)
    const switchedBox: RawBox = rawBox.length >= 5
      ? [rawBox[0], rawBox[1], rawBox[2], rawBox[3], rawBox[4]]
      : [rawBox[0], rawBox[1], rawBox[2], rawBox[3]]

    let newPositiveBoxes: RawBox[]
    let newNegativeBoxes: RawBox[]

    if (detection.type === 'positive') {
      // Positive -> Negative
      newPositiveBoxes = positiveBoxes.filter((_, idx) => idx !== detection.index)
      newNegativeBoxes = [...negativeBoxes, switchedBox]
    } else {
      // Negative -> Positive
      newNegativeBoxes = negativeBoxes.filter((_, idx) => idx !== detection.index)
      newPositiveBoxes = [...positiveBoxes, switchedBox]
    }

    // Mise à jour optimiste: mettre à jour l'UI immédiatement
    setLocalPositiveBoxes(newPositiveBoxes)
    setLocalNegativeBoxes(newNegativeBoxes)

    // Notifier le parent pour mettre à jour les statistiques (avec confidence si présente)
    onBoxLabelChange?.(
      imageIndex,
      newPositiveBoxes.length,
      newNegativeBoxes.length,
      newPositiveBoxes,
      newNegativeBoxes
    )

    // Sauvegarder dans la base de données en arrière-plan si on a un ID d'image
    if (result?.id) {
      try {
        await updateBoxes({
          imageId: result.id,
          positiveBoxes: newPositiveBoxes.map((b) => [b[0], b[1], b[2], b[3]]),
          negativeBoxes: newNegativeBoxes.map((b) => [b[0], b[1], b[2], b[3]]),
        })
        console.log('✅ Boxes sauvegardées avec succès dans la base de données')
      } catch (error) {
        console.error('❌ Erreur lors de la sauvegarde des boxes:', error)
      }
    }
  }, [positiveBoxes, negativeBoxes, imageIndex, onBoxLabelChange, result?.id, updateBoxes])

  const stats = useMemo(() => ({
    positive: positiveBoxes.length,
    negative: negativeBoxes.length,
    total: positiveBoxes.length + negativeBoxes.length,
    displayedPositive: showPositive ? positiveBoxes.length : 0,
    displayedNegative: showNegative && !showOnlyInfected ? negativeBoxes.length : 0,
  }), [positiveBoxes.length, negativeBoxes.length, showPositive, showNegative, showOnlyInfected])

  return (
    <>
      <Card variant="outline" borderColor="purple.200" bg="white" boxShadow="md">
        <CardHeader pb={2} bg="purple.50">
          <HStack justify="space-between">
            <HStack spacing={3}>
              <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>
                {t('lab.parasitology.results.image')} #{imageIndex + 1}
              </Badge>
              {isAnalyzing && (
                <Badge colorScheme="blue" variant="subtle" display="flex" alignItems="center" gap={1}>
                  <Box
                    as="span"
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg="blue.400"
                    display="inline-block"
                    animation="pulse 1.5s ease-in-out infinite"
                    sx={{ '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }}
                  />
                  Analyse en cours...
                </Badge>
              )}
              {result && (
                <Badge colorScheme="green" variant="subtle">Terminé</Badge>
              )}
              {!result && !isAnalyzing && (
                <Badge colorScheme="gray">En attente</Badge>
              )}
            </HStack>
            <HStack spacing={2}>
              <Tooltip label={t('lab.parasitology.results.imageInfo')}>
                <IconButton
                  aria-label="Image info"
                  icon={<MdInfo />}
                  size="sm"
                  variant="ghost"
                  colorScheme="purple"
                  onClick={onOpen}
                />
              </Tooltip>
              <Tooltip label={t('lab.parasitology.results.fullscreen')}>
                <IconButton
                  aria-label="Fullscreen"
                  icon={<MdFullscreen />}
                  size="sm"
                  variant="ghost"
                  colorScheme="purple"
                  onClick={onOpen}
                />
              </Tooltip>
            </HStack>
          </HStack>
        </CardHeader>
        <Divider />
        <CardBody>
          <VStack align="stretch" spacing={4}>
            {/* Controls */}
            <Card variant="filled" bg="gray.50">
              <CardBody py={3}>
                <VStack spacing={3} align="stretch">
                  {/* Zoom Controls */}
                  <HStack justify="space-between" align="center">
                    <HStack spacing={2} flex="1">
                      <Text fontSize="xs" fontWeight="semibold" color="gray.600" minW="60px">
                        Zoom: {zoom.toFixed(1)}x
                      </Text>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        aria-label="Zoom out"
                        icon={<MdZoomOut />}
                        onClick={() => setZoom(prev => Math.max(0.5, prev - 0.25))}
                        isDisabled={zoom <= 0.5}
                      />
                      <Slider
                        value={zoom}
                        onChange={setZoom}
                        min={0.5}
                        max={5}
                        step={0.1}
                        flex="1"
                      >
                        <SliderTrack bg="purple.100">
                          <SliderFilledTrack bg="purple.500" />
                        </SliderTrack>
                        <SliderThumb boxSize={4} />
                      </Slider>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        aria-label="Zoom in"
                        icon={<MdZoomIn />}
                        onClick={() => setZoom(prev => Math.min(5, prev + 0.25))}
                        isDisabled={zoom >= 5}
                      />
                      <IconButton
                        size="xs"
                        variant="ghost"
                        aria-label="Reset view"
                        icon={<MdCenterFocusStrong />}
                        onClick={resetView}
                      />
                    </HStack>
                  </HStack>

                  {/* Filter Controls */}
                  {result && (
                    <HStack spacing={4} fontSize="sm">
                      <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                        Filtres :
                      </Text>
                      <FormControl display="flex" alignItems="center" w="auto">
                        <FormLabel htmlFor="show-positive" mb="0" fontSize="xs" mr={2}>
                          <HStack spacing={1}>
                            <Box w={3} h={3} bg="orange.400" borderRadius="sm" />
                            <Text>Infectées ({stats.positive})</Text>
                          </HStack>
                        </FormLabel>
                        <Switch
                          id="show-positive"
                          size="sm"
                          colorScheme="orange"
                          isChecked={showPositive}
                          onChange={(e) => setShowPositive(e.target.checked)}
                        />
                      </FormControl>
                      {!showOnlyInfected && (
                        <FormControl display="flex" alignItems="center" w="auto">
                          <FormLabel htmlFor="show-negative" mb="0" fontSize="xs" mr={2}>
                            <HStack spacing={1}>
                              <Box w={3} h={3} bg="green.400" borderRadius="sm" />
                              <Text>Saines ({stats.negative})</Text>
                            </HStack>
                          </FormLabel>
                          <Switch
                            id="show-negative"
                            size="sm"
                            colorScheme="green"
                            isChecked={showNegative}
                            onChange={(e) => setShowNegative(e.target.checked)}
                          />
                        </FormControl>
                      )}
                    </HStack>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* Image Viewer with Detections */}
            <Box
              ref={containerRef}
              bg="gray.50"
              borderRadius="lg"
              overflow="hidden"
              position="relative"
              border="2px solid"
              borderColor={isAnalyzing ? 'blue.300' : 'purple.200'}
              cursor={draggingBoxInfo?.hasMoved ? 'grabbing' : isDragging ? 'grabbing' : 'grab'}
              userSelect="none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* Analyzing overlay */}
              {isAnalyzing && (
                <Box
                  position="absolute"
                  inset={0}
                  zIndex={20}
                  bg="whiteAlpha.700"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={3}
                  backdropFilter="blur(1px)"
                >
                  <Box
                    w="40px"
                    h="40px"
                    border="3px solid"
                    borderColor="purple.200"
                    borderTopColor="purple.500"
                    borderRadius="full"
                    animation="spin 0.8s linear infinite"
                    sx={{ '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }}
                  />
                  <Text fontSize="sm" fontWeight="semibold" color="purple.600">
                    Analyse IA en cours...
                  </Text>
                </Box>
              )}

              <Box
                position="absolute"
                top="8px"
                left="8px"
                zIndex={10}
                bg="blackAlpha.700"
                px={3}
                py={1}
                borderRadius="md"
                fontSize="xs"
                color="white"
              >
                {isDragging ? 'Dragging...' : 'Drag to pan · Use slider to zoom'}
              </Box>

              <Box
                transform={`translate(${pan.x}px, ${pan.y}px) scale(${zoom})`}
                transformOrigin="center"
                transition={isDragging ? 'none' : 'transform 0.1s ease-out'}
                display="inline-block"
                position="relative"
              >
                {resolvedImageUrl ? (
                  <Image
                    ref={imageRef}
                    src={resolvedImageUrl}
                    alt={`Microscope capture ${imageIndex + 1}`}
                    maxW="100%"
                    maxH="70vh"
                    objectFit="contain"
                    display="block"
                    onLoad={(event) => {
                      const img = event.currentTarget
                      setDimensions({
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                      })
                    }}
                    onError={() => setFallbackError(true)}
                    pointerEvents="none"
                  />
                ) : (
                  <VStack
                    minH="200px"
                    align="center"
                    justify="center"
                    spacing={2}
                    p={4}
                  >
                    <Box
                      w="32px"
                      h="32px"
                      border="3px solid"
                      borderColor="purple.200"
                      borderTopColor="purple.500"
                      borderRadius="full"
                      animation="spin 0.8s linear infinite"
                      sx={{ '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }}
                    />
                    <Text fontSize="sm" color="gray.500">Chargement de l'image…</Text>
                  </VStack>
                )}

                {fallbackError && (
                  <VStack
                    position="absolute"
                    inset={0}
                    bg="gray.100"
                    align="center"
                    justify="center"
                    spacing={2}
                  >
                    <MdScience style={{ fontSize: '48px', color: 'var(--chakra-colors-gray-500)' }} />
                    <Text fontSize="sm" color="gray.600">
                      {t('lab.parasitology.results.unableToLoadImage')}
                    </Text>
                  </VStack>
                )}

                {/* Render bounding boxes */}
                {/* Note: Bounding boxes already include padding (MALARIA_BBOX_PADDING = 4px) applied by backend */}
                {/* See: careflai-be/src/services/ai.js adjustBoundingBoxes() function */}
                {!fallbackError && dimensions.width > 0 && allDetections.map((detection, idx) => {
                  const [x1, y1, x2, y2] = detection.coords
                  const imgEl = imageRef.current

                  if (!imgEl) return null

                  // CRITICAL: Use getBoundingClientRect for accurate dimensions
                  // offsetWidth/Height can include padding and borders
                  const imgRect = imgEl.getBoundingClientRect()
                  const renderedWidth = imgEl.width || imgRect.width
                  const renderedHeight = imgEl.height || imgRect.height

                  // Convert coordinates to pixels (already padded by backend)
                  let pixelX1: number, pixelY1: number, pixelX2: number, pixelY2: number

                  if (areCoordinatesNormalized) {
                    // Normalized coordinates (0-1) → scale to rendered size
                    pixelX1 = x1 * renderedWidth
                    pixelY1 = y1 * renderedHeight
                    pixelX2 = x2 * renderedWidth
                    pixelY2 = y2 * renderedHeight
                  } else {
                    // Absolute coordinates → scale from natural to rendered size
                    const scaleX = renderedWidth / imageDimensions.width
                    const scaleY = renderedHeight / imageDimensions.height
                    pixelX1 = x1 * scaleX
                    pixelY1 = y1 * scaleY
                    pixelX2 = x2 * scaleX
                    pixelY2 = y2 * scaleY
                  }

                  const boxWidth = Math.abs(pixelX2 - pixelX1)
                  const boxHeight = Math.abs(pixelY2 - pixelY1)
                  const boxLeft = Math.min(pixelX1, pixelX2)
                  const boxTop = Math.min(pixelY1, pixelY2)

                  const isHovered = hoveredBox === idx
                  const color = detection.type === 'positive' ? 'orange' : 'green'
                  const colorValue = detection.type === 'positive' ? '#FF6B35' : '#38B2AC'

                  // Apply drag offset if this box is being dragged
                  const isDraggingThis = draggingBoxInfo?.hasMoved
                    && draggingBoxInfo.detection.index === detection.index
                    && draggingBoxInfo.detection.type === detection.type
                  const renderLeft = isDraggingThis ? boxLeft + dragOffsetPx.dx / zoom : boxLeft
                  const renderTop = isDraggingThis ? boxTop + dragOffsetPx.dy / zoom : boxTop

                  return (
                    <Tooltip
                      key={`detection-${detection.type}-${idx}`}
                      label={
                        <VStack align="start" spacing={1} fontSize="xs">
                          <Text fontWeight="bold">
                            {detection.type === 'positive' ? 'Cellule infectée' : 'Cellule saine'}
                          </Text>
                          <Text>
                            Confidence:{' '}
                            {detection.confidence !== undefined
                              ? `${(detection.confidence * 100).toFixed(1)}%`
                              : 'N/A'}
                          </Text>
                          <Text>Index: {detection.index}</Text>
                          <Text fontSize="2xs" color="gray.300">
                            Box: [{x1.toFixed(1)}, {y1.toFixed(1)}, {x2.toFixed(1)}, {y2.toFixed(1)}]
                          </Text>
                          <Divider my={1} />
                          <Text fontSize="2xs" fontWeight="bold" color="yellow.300">
                            Click to change label · Drag to reposition
                          </Text>
                        </VStack>
                      }
                      isOpen={isHovered && !draggingBoxInfo}
                      placement="top"
                    >
                      <Box
                        position="absolute"
                        left={`${renderLeft}px`}
                        top={`${renderTop}px`}
                        width={`${boxWidth}px`}
                        height={`${boxHeight}px`}
                        border={isHovered ? '3px solid' : '2px solid'}
                        borderColor={colorValue}
                        borderRadius="2px"
                        boxShadow={isDraggingThis ? `0 0 16px ${colorValue}` : isHovered ? `0 0 12px ${colorValue}` : `0 0 4px rgba(0,0,0,0.3)`}
                        bg={isHovered ? `${color}.100` : 'transparent'}
                        opacity={isDraggingThis ? 0.75 : isHovered ? 1 : 0.85}
                        transition={isDraggingThis ? 'none' : 'all 0.2s'}
                        pointerEvents="auto"
                        cursor={isDraggingThis ? 'grabbing' : 'grab'}
                        zIndex={isDraggingThis ? 1000 : undefined}
                        onMouseDown={(e) => handleBoxMouseDown(detection, e)}
                        onMouseEnter={() => setHoveredBox(idx)}
                        onMouseLeave={() => setHoveredBox(null)}
                        onClick={(e) => handleBoxClick(detection, e)}
                        _hover={{
                          transform: draggingBoxInfo ? undefined : 'scale(1.05)',
                          zIndex: 1000,
                        }}
                      />
                    </Tooltip>
                  )
                })}
              </Box>
            </Box>

            {/* Info Footer */}
            <HStack justify="space-between" fontSize="xs" color="gray.600">
              <Text>
                {allDetections.length} détection{allDetections.length !== 1 ? 's' : ''} affichée{allDetections.length !== 1 ? 's' : ''}
              </Text>
              <Text>
                {dimensions.width > 0 ? `${dimensions.width} x ${dimensions.height} px` : 'Image en cours de chargement…'}
              </Text>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Info Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent borderRadius="xl" overflow="hidden">
          <Box bg="purple.600" px={6} py={4}>
            <HStack spacing={3}>
              <Box bg="whiteAlpha.200" p={2} borderRadius="lg">
                <MdScience size={20} color="white" />
              </Box>
              <VStack align="start" spacing={0}>
                <Heading size="sm" color="white">
                  {t('lab.parasitology.results.image')} #{imageIndex + 1}
                </Heading>
                <Text fontSize="xs" color="whiteAlpha.800">
                  {dimensions.width > 0 ? `${dimensions.width} x ${dimensions.height} px` : 'Chargement…'}
                </Text>
              </VStack>
            </HStack>
          </Box>
          <ModalCloseButton color="white" />

          <ModalBody py={5} px={6}>
            <VStack spacing={4} align="stretch">
              {/* Detection counts */}
              <SimpleGrid columns={3} spacing={3}>
                <Box textAlign="center" bg="gray.50" p={3} borderRadius="lg">
                  <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats.total}</Text>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">Total</Text>
                </Box>
                <Box textAlign="center" bg="orange.50" p={3} borderRadius="lg" border="1px solid" borderColor="orange.100">
                  <Text fontSize="2xl" fontWeight="bold" color="orange.500">{stats.positive}</Text>
                  <Text fontSize="xs" color="orange.600" fontWeight="medium">Infectées</Text>
                </Box>
                <Box textAlign="center" bg="green.50" p={3} borderRadius="lg" border="1px solid" borderColor="green.100">
                  <Text fontSize="2xl" fontWeight="bold" color="green.500">{stats.negative}</Text>
                  <Text fontSize="xs" color="green.600" fontWeight="medium">Saines</Text>
                </Box>
              </SimpleGrid>

              {/* Parasitemia rate */}
              {result && (
                <Box bg="purple.50" p={4} borderRadius="lg" border="1px solid" borderColor="purple.100">
                  <HStack justify="space-between" align="center">
                    <Text fontSize="sm" color="purple.700" fontWeight="medium">Parasitémie</Text>
                    <Text fontSize="xl" fontWeight="bold" color="purple.600">
                      {result.parasitemiaPercent?.toFixed(2)}%
                    </Text>
                  </HStack>
                  <Box mt={2} bg="purple.100" borderRadius="full" h="6px" overflow="hidden">
                    <Box
                      bg="purple.500"
                      h="100%"
                      borderRadius="full"
                      w={`${Math.min((result.parasitemiaPercent ?? 0), 100)}%`}
                      transition="width 0.3s ease"
                    />
                  </Box>
                </Box>
              )}

            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

interface MicroscopeEvidenceGridProps {
  imagePreviews: string[]
  individualResults?: IAnalysisMalaria[]
  showOnlyInfected?: boolean
  title?: string
  emptyMessage?: string
  highlightBothStates?: boolean
  analyzingImageIndex?: number
  onBoxLabelChange?: (
    imageIndex: number,
    newPositiveCount: number,
    newNegativeCount: number,
    newPositiveBoxes: number[][],
    newNegativeBoxes: number[][]
  ) => void
}

export default function MicroscopeEvidenceGrid({
  imagePreviews,
  individualResults,
  showOnlyInfected = false,
  title,
  emptyMessage,
  highlightBothStates = true,
  analyzingImageIndex = -1,
  onBoxLabelChange,
}: MicroscopeEvidenceGridProps) {
  const { t } = useTranslation()
  const defaultTitle = title || t('lab.parasitology.results.microscopeEvidence')
  const defaultEmptyMessage = emptyMessage || t('lab.parasitology.results.noPatternPositiveCells')

  const imageData = useMemo(() => {
    if (!imagePreviews || imagePreviews.length === 0) return []

    return imagePreviews.map((imageUrl, index) => {
      const result = individualResults?.[index]
      return {
        imageUrl,
        result,
        imageIndex: index,
        positiveCount: result?.positiveBoxes?.length ?? 0,
      }
    })
  }, [imagePreviews, individualResults])

  if (imageData.length === 0) {
    return null
  }

  const visibleImages = showOnlyInfected
    ? imageData.filter((item) => item.positiveCount > 0)
    : imageData

  return (
    <Card boxShadow="lg">
      <CardHeader bg="purple.50">
        <HStack justify="space-between">
          <Heading size="sm" color="purple.700">{defaultTitle}</Heading>
          <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
            {visibleImages.length} image{visibleImages.length !== 1 ? 's' : ''}
          </Badge>
        </HStack>
      </CardHeader>
      <Divider />
      <CardBody>
        {visibleImages.length > 0 ? (
          <VStack spacing={6} align="stretch">
            {visibleImages.map((item, idx) => (
              <AnalysisImagePreview
                key={`${item.imageIndex}-${item.imageUrl}-${item.result?.id ?? 'pending'}-${idx}`}
                imageUrl={item.imageUrl}
                result={item.result}
                imageIndex={item.imageIndex}
                showOnlyInfected={showOnlyInfected}
                highlightBothStates={highlightBothStates}
                isAnalyzing={analyzingImageIndex === item.imageIndex}
                onBoxLabelChange={onBoxLabelChange}
              />
            ))}
          </VStack>
        ) : (
          <VStack spacing={2} py={8} color="gray.500">
            <MdScience style={{ fontSize: '48px' }} />
            <Text fontSize="sm" textAlign="center">
              {defaultEmptyMessage}
            </Text>
          </VStack>
        )}
      </CardBody>
    </Card>
  )
}
